import { useState, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';

interface UploadZoneProps {
  onUpload: (files: File[]) => void;
  isFreePlan?: boolean;
}

export default function UploadZone({ onUpload, isFreePlan = false }: UploadZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [previews, setPreviews] = useState<{ name: string; url: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFiles = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (fileArray.length === 0) return;

      if (isFreePlan) {
        Swal.fire({
          title: 'Plan Free limitado',
          html: '<p class="text-sm text-foreground-500 leading-relaxed">Tu plan Free no incluye fotos. Actualiza a Pro.</p>',
          icon: 'warning',
          confirmButtonText: 'Ver planes',
          confirmButtonColor: '#f59e0b',
          showCancelButton: true,
          cancelButtonText: 'Cerrar',
          cancelButtonColor: '#9ca3af',
          customClass: {
            popup: 'rounded-2xl',
            title: 'font-bold',
            confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
            cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
            htmlContainer: 'text-sm',
          },
          buttonsStyling: false,
        });
        return;
      }

      const newPreviews = fileArray.map((f) => ({
        name: f.name,
        url: URL.createObjectURL(f),
      }));
      setPreviews((prev) => [...prev, ...newPreviews]);
      onUpload(fileArray);
    },
    [onUpload, isFreePlan]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) processFiles(files);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) processFiles(files);
  };

  const removePreview = (index: number) => {
    setPreviews((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      if (index < prev.length) URL.revokeObjectURL(prev[index].url);
      return updated;
    });
  };

  const clearAll = () => {
    previews.forEach((p) => URL.revokeObjectURL(p.url));
    setPreviews([]);
  };

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`p-8 border-2 border-dashed rounded-2xl m-3 transition-all cursor-pointer ${
          isFreePlan
            ? 'border-amber-300/50 bg-amber-50/30'
            : isDragging
              ? 'border-accent-400 bg-accent-50/50'
              : 'border-background-300 hover:border-accent-300 hover:bg-accent-50/30'
        }`}
        onClick={() => {
          if (isFreePlan) {
            Swal.fire({
              title: 'Plan Free limitado',
              html: '<p class="text-sm text-foreground-500 leading-relaxed">Tu plan Free no incluye fotos. Actualiza a Pro.</p>',
              icon: 'warning',
              confirmButtonText: 'Ver planes',
              confirmButtonColor: '#f59e0b',
              showCancelButton: true,
              cancelButtonText: 'Cerrar',
              cancelButtonColor: '#9ca3af',
              customClass: {
                popup: 'rounded-2xl',
                title: 'font-bold',
                confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
                cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
                htmlContainer: 'text-sm',
              },
              buttonsStyling: false,
            });
            return;
          }
          fileInputRef.current?.click();
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />

        <div className="flex flex-col items-center gap-3">
          <div
            className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all ${
              isFreePlan
                ? 'bg-amber-100'
                : isDragging
                  ? 'bg-accent-100 scale-110'
                  : 'bg-background-100'
            }`}
          >
            <i
              className={`text-2xl transition-all ${
                isFreePlan
                  ? 'ri-lock-line text-amber-500'
                  : isDragging
                    ? 'text-accent-600 ri-file-upload-line'
                    : 'text-foreground-400 ri-upload-cloud-2-line'
              }`}
            ></i>
          </div>
          <div className="text-center">
            <p className="text-sm font-bold text-foreground-800">
              {isFreePlan
                ? 'Plan Free — Sin acceso a fotos'
                : isDragging
                  ? '¡Suelta las fotos aquí!'
                  : 'Arrastra tus fotos aquí'}
            </p>
            <p className="text-xs text-foreground-400 mt-1">
              {isFreePlan
                ? 'Actualiza a Pro para subir fotos y crear álbumes'
                : 'o haz clic para seleccionar archivos'}
            </p>
          </div>
          <p className="text-[10px] text-foreground-400/70">
            JPG, PNG, WebP &bull; Máx. 25 MB por archivo
          </p>
        </div>
      </div>

      {/* Botón subir */}
      <div className="flex items-center gap-2 px-3 pb-3">
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (isFreePlan) {
              Swal.fire({
                title: 'Plan Free limitado',
                html: '<p class="text-sm text-foreground-500 leading-relaxed">Tu plan Free no incluye fotos. Actualiza a Pro.</p>',
                icon: 'warning',
                confirmButtonText: 'Ver planes',
                confirmButtonColor: '#f59e0b',
                showCancelButton: true,
                cancelButtonText: 'Cerrar',
                cancelButtonColor: '#9ca3af',
                customClass: {
                  popup: 'rounded-2xl',
                  title: 'font-bold',
                  confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
                  cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
                  htmlContainer: 'text-sm',
                },
                buttonsStyling: false,
              });
              return;
            }
            fileInputRef.current?.click();
          }}
          className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${
            isFreePlan
              ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
              : 'bg-primary-500 text-background-50 hover:bg-primary-600'
          }`}
        >
          <i className={`text-base ${isFreePlan ? 'ri-lock-line' : 'ri-image-add-line'}`}></i>
          {isFreePlan ? 'Plan Free — Bloqueado' : 'Subir fotos'}
        </button>
        {previews.length > 0 && (
          <button
            onClick={clearAll}
            className="px-3 py-2.5 rounded-xl text-xs font-semibold text-foreground-500 hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
          >
            Limpiar todo
          </button>
        )}
        {previews.length > 0 && (
          <span className="text-xs text-foreground-400 ml-auto">
            {previews.length} {previews.length === 1 ? 'archivo listo' : 'archivos listos'}
          </span>
        )}
      </div>

      {/* Previews */}
      {previews.length > 0 && (
        <div className="border-t border-background-200 px-3 py-3">
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
            {previews.map((p, i) => (
              <div key={i} className="relative group aspect-square rounded-xl overflow-hidden bg-background-200">
                <img src={p.url} alt={p.name} className="w-full h-full object-cover object-top" />
                <button
                  onClick={() => removePreview(i)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/50 flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <i className="ri-close-line text-background-50 text-[10px]"></i>
                </button>
                <p className="absolute bottom-0 inset-x-0 bg-black/50 px-1.5 py-0.5 text-background-50 text-[8px] truncate">
                  {p.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}